import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 React icons
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

import {
  approvePendingUser,
  deleteUser,
  getUser,
  getUserOnboardingOptions,
  getUserReferenceData,
  rejectPendingUser,
  updateUser,
} from "../api/users";

const EDITABLE_FIELDS = [
  { key: "full_name", label: "Full name" },
  { key: "email", label: "Email" },
  { key: "id_number", label: "ID number" },
  { key: "branch_id", label: "Branch" },
  { key: "job_title_id", label: "Job title" },
];

function buildPatch(original, edited) {
  const patch = {};
  EDITABLE_FIELDS.forEach(({ key }) => {
    if (edited[key] !== original[key]) {
      patch[key] = edited[key];
    }
  });
  return patch;
}

function normalizeFormValue(key, value) {
  if (value === "") return null;
  if (["role_id", "branch_id", "job_title_id"].includes(key)) {
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : null;
  }
  return value;
}

function UserDetailsModal({ open, onClose, userId, pendingUser, onUpdated }) {
  const isPending = !!pendingUser;

  const [isEditMode, setIsEditMode] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [originalUser, setOriginalUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [approveData, setApproveData] = useState({
    full_name: "",
    email: "",
    branch_id: "",
    job_title_id: "",
    id_number: "",
  });

  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  const [options, setOptions] = useState({ roles: [], branches: [], job_titles: [] });

  const title = useMemo(() => {
    if (isPending) return "Pending user";
    return "User details";
  }, [isPending]);

  useEffect(() => {
    if (!open) return;

    setError(null);
    setSuccess(null);
    setIsEditMode(false);
    setIsApproving(false);

    if (isPending) {
      setOriginalUser(null);
      setFormData({
        name: pendingUser?.name || pendingUser?.full_name || "",
        email: pendingUser?.email || "",
        status: pendingUser?.status || "pending",
        created_at: pendingUser?.created_at || null,
      });
      setApproveData({
        full_name: pendingUser?.name || pendingUser?.full_name || "",
        email: pendingUser?.email || "",
        branch_id: "",
        job_title_id: "",
        id_number: "",
      });
      return;
    }

    if (!userId) return;

    setLoading(true);
    getUser(userId)
      .then((data) => {
        const user = data.user || data;
        setOriginalUser(user);
        setFormData({
          full_name: user.full_name ?? "",
          email: user.email ?? "",
          id_number: user.id_number ?? "",
          branch_id: user.branch_id ?? "",
          job_title_id: user.job_title_id ?? "",
          branch_name: user.branch_name ?? "",
          job_title_name: user.job_title_name ?? "",
        });
      })
      .catch((e) => {
        setError(e?.message || "Failed to fetch user");
      })
      .finally(() => setLoading(false));
  }, [open, userId, isPending, pendingUser]);

  useEffect(() => {
    if (!open || isPending) return;
    if (!isEditMode) return;
    // Load options for branch/job title dropdowns
    setOptionsLoading(true);
    setOptionsError(null);
    getUserReferenceData()
      .then((data) => {
        setOptions({
          branches: data.branches || [],
          job_titles: data.job_titles || [],
          roles: [],
        });
      })
      .catch((e) => setOptionsError(e?.message || "Failed to load options"))
      .finally(() => setOptionsLoading(false));
  }, [open, isPending, isEditMode]);

  useEffect(() => {
    if (!open || !isPending || !isApproving) return;
    if (!pendingUser?.id) return;

    setOptionsLoading(true);
    setOptionsError(null);
    getUserOnboardingOptions()
      .then((data) => {
        setOptions({
          roles: data.roles || [],
          branches: data.branches || [],
          job_titles: data.job_titles || [],
        });
      })
      .catch((e) => {
        setOptionsError(e?.message || "Failed to load onboarding options");
      })
      .finally(() => setOptionsLoading(false));
  }, [open, isPending, isApproving, pendingUser?.id]);

  const handleChange = (key) => (e) => {
    const value = normalizeFormValue(key, e.target.value);
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleApproveChange = (key) => (e) => {
    const value = normalizeFormValue(key, e.target.value);
    setApproveData((prev) => ({ ...prev, [key]: value }));
  };

  const handleReject = async () => {
    if (!pendingUser?.id) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await rejectPendingUser(pendingUser.id);
      setSuccess("Pending user rejected.");
      if (onUpdated) onUpdated({ type: "pending_rejected", id: pendingUser.id });
      onClose();
    } catch (e) {
      setError(e?.message || "Failed to reject pending user");
    } finally {
      setSaving(false);
    }
  };

  const handleApproveSubmit = async () => {
    if (!pendingUser?.id) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        full_name: approveData.full_name,
        email: approveData.email,
        branch_id: approveData.branch_id ?? null,
        job_title_id: approveData.job_title_id ?? null,
        id_number: approveData.id_number || null,
      };
      const res = await approvePendingUser(pendingUser.id, payload);
      setSuccess("User approved and onboarded.");
      if (onUpdated) onUpdated({ type: "pending_approved", pending_id: pendingUser.id, user: res.user });
      onClose();
    } catch (e) {
      setError(e?.message || "Failed to approve pending user");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!originalUser || !userId) return;
    setSaving(true);
    setError(null);
    try {
      const patch = buildPatch(
        {
          full_name: originalUser.full_name ?? "",
          email: originalUser.email ?? "",
          id_number: originalUser.id_number ?? null,
          branch_id: originalUser.branch_id ?? null,
          job_title_id: originalUser.job_title_id ?? null,
        },
        formData
      );

      const updated = await updateUser(userId, patch);
      const updatedUser = updated.user || updated;
      setOriginalUser(updatedUser);
      setIsEditMode(false);
      if (onUpdated) onUpdated(updatedUser);
    } catch (e) {
      setError(e?.message || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!userId) return;
    setSaving(true);
    setError(null);
    try {
      await deleteUser(userId);
      if (onUpdated) onUpdated({ type: "user_deleted", id: userId });
      onClose();
    } catch (e) {
      setError(e?.message || "Failed to delete user");
    } finally {
      setSaving(false);
      setIsDeleteConfirmOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <MDBox>
          <MDTypography variant="h6">{title}</MDTypography>
          {!isPending && (
            <MDTypography variant="caption" color="text">
              {formData?.branch_name ? `${formData.branch_name} • ` : ""}
              {formData?.job_title_name || ""}
            </MDTypography>
          )}
        </MDBox>
        <MDBox display="flex" alignItems="center" gap={1}>
          {!isPending && (
            <MDButton
              variant="text"
              color="info"
              onClick={() => setIsEditMode((v) => !v)}
              startIcon={<EditIcon />}
              disabled={loading || saving}
            >
              {isEditMode ? "View" : "Edit"}
            </MDButton>
          )}
          <IconButton onClick={onClose} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </MDBox>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <MDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </MDBox>
          ) : error ? (
            <MDTypography variant="body2" color="error">
              {error}
            </MDTypography>
          ) : isPending ? (
            <MDBox display="flex" flexDirection="column" gap={2}>
              {success && (
                <MDTypography variant="body2" color="success">
                  {success}
                </MDTypography>
              )}
              <MDInput label="Name" value={formData.name || ""} disabled fullWidth />
              <MDInput label="Email" value={formData.email || ""} disabled fullWidth />
              <MDInput label="Status" value={formData.status || ""} disabled fullWidth />
              <MDInput
                label="Requested"
                value={formData.created_at ? new Date(formData.created_at).toLocaleString() : "—"}
                disabled
                fullWidth
              />

              {!isApproving ? (
                <MDBox display="flex" gap={1} mt={1}>
                  <MDButton
                    color="success"
                    variant="gradient"
                    onClick={() => setIsApproving(true)}
                    disabled={saving}
                  >
                    Approve
                  </MDButton>
                  <MDButton
                    color="error"
                    variant="gradient"
                    onClick={handleReject}
                    disabled={saving}
                  >
                    Reject
                  </MDButton>
                </MDBox>
              ) : (
                <MDBox mt={2} display="flex" flexDirection="column" gap={2}>
                  <MDTypography variant="button" fontWeight="medium">
                    Account settings
                  </MDTypography>

                  {optionsLoading ? (
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={18} />
                      <MDTypography variant="caption">Loading options…</MDTypography>
                    </MDBox>
                  ) : optionsError ? (
                    <MDBox>
                      <MDTypography variant="caption" color="error">
                        {optionsError}
                      </MDTypography>
                      <MDBox mt={1}>
                        <MDButton variant="text" color="info" onClick={() => setIsApproving(true)}>
                          Retry
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  ) : (
                    <>
                    <MDInput
                      label="Full name"
                      value={approveData.full_name ?? ""}
                      onChange={handleApproveChange("full_name")}
                      fullWidth
                    />
                    <MDInput
                      label="Email"
                      value={approveData.email ?? ""}
                      onChange={handleApproveChange("email")}
                      fullWidth
                    />

                    <MDInput
                      select
                      label="Branch"
                      value={approveData.branch_id ?? ""}
                      onChange={handleApproveChange("branch_id")}
                      fullWidth
                      SelectProps={{ native: true }}
                    >
                      <option value="" />
                      {options.branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.legal_name ?? b.name ?? b.id}
                        </option>
                      ))}
                    </MDInput>

                    <MDInput
                      select
                      label="Job title"
                      value={approveData.job_title_id ?? ""}
                      onChange={handleApproveChange("job_title_id")}
                      fullWidth
                      SelectProps={{ native: true }}
                    >
                      <option value="" />
                      {options.job_titles.map((jt) => (
                        <option key={jt.id} value={jt.id}>
                          {jt.name ?? jt.id}
                        </option>
                      ))}
                    </MDInput>

                    <MDInput
                      label="ID number"
                      value={approveData.id_number ?? ""}
                      onChange={handleApproveChange("id_number")}
                      fullWidth
                    />

                    <MDBox display="flex" gap={1} mt={1}>
                      <MDButton color="dark" variant="text" onClick={() => setIsApproving(false)} disabled={saving}>
                        Cancel
                      </MDButton>
                      <MDButton
                        color="success"
                        variant="gradient"
                        onClick={handleApproveSubmit}
                        disabled={saving || !approveData.full_name}
                      >
                        {saving ? "Submitting..." : "Submit"}
                      </MDButton>
                    </MDBox>
                  </>
                  )}
                </MDBox>
              )}
            </MDBox>
          ) : (
            <MDBox display="flex" flexDirection="column" gap={2}>
              <MDInput
                label="Full name"
                value={formData.full_name ?? ""}
                onChange={handleChange("full_name")}
                disabled={!isEditMode || saving}
                fullWidth
              />
              <MDInput
                label="Email"
                value={formData.email ?? ""}
                onChange={handleChange("email")}
                disabled={!isEditMode || saving}
                fullWidth
              />
              <MDInput
                label="ID number"
                value={formData.id_number ?? ""}
                onChange={handleChange("id_number")}
                disabled={!isEditMode || saving}
                fullWidth
              />

              {isEditMode ? (
                <>
                  {optionsLoading ? (
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={18} />
                      <MDTypography variant="caption">Loading options…</MDTypography>
                    </MDBox>
                  ) : optionsError ? (
                    <MDTypography variant="caption" color="error">
                      {optionsError}
                    </MDTypography>
                  ) : (
                    <>
                      <MDInput
                        select
                        label="Branch"
                        value={formData.branch_id ?? ""}
                        onChange={handleChange("branch_id")}
                        fullWidth
                        SelectProps={{ native: true }}
                        disabled={saving}
                      >
                        <option value="" />
                        {options.branches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.legal_name ?? b.name ?? b.id}
                          </option>
                        ))}
                      </MDInput>

                      <MDInput
                        select
                        label="Job title"
                        value={formData.job_title_id ?? ""}
                        onChange={handleChange("job_title_id")}
                        fullWidth
                        SelectProps={{ native: true }}
                        disabled={saving}
                      >
                        <option value="" />
                        {options.job_titles.map((jt) => (
                          <option key={jt.id} value={jt.id}>
                            {jt.name ?? jt.id}
                          </option>
                        ))}
                      </MDInput>
                    </>
                  )}
                </>
              ) : (
                <>
                  <MDInput label="Branch" value={formData.branch_name ?? ""} disabled fullWidth />
                  <MDInput label="Job title" value={formData.job_title_name ?? ""} disabled fullWidth />
                </>
              )}
            </MDBox>
          )}
        </DialogContent>

      {!isPending && (
        <DialogActions>
          <MDButton
            onClick={() => setIsDeleteConfirmOpen(true)}
            color="error"
            variant="text"
            disabled={saving}
          >
            Delete user
          </MDButton>
          <MDButton onClick={onClose} color="dark" variant="text" disabled={saving}>
            Close
          </MDButton>
          <MDButton
            onClick={handleSave}
            color="info"
            variant="gradient"
            disabled={!isEditMode || saving || loading}
          >
            {saving ? "Saving..." : "Save changes"}
          </MDButton>
        </DialogActions>
      )}
      </Dialog>

      <DeleteUserConfirmDialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        saving={saving}
      />
    </>
  );
}

function DeleteUserConfirmDialog({ open, onClose, onConfirm, saving }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete user?</DialogTitle>
      <DialogContent dividers>
        <MDTypography variant="body2" color="text">
          This will remove the user from the system. This action cannot be undone.
        </MDTypography>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="dark" variant="text" disabled={saving}>
          Cancel
        </MDButton>
        <MDButton onClick={onConfirm} color="error" variant="gradient" disabled={saving}>
          {saving ? "Deleting..." : "Delete"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

DeleteUserConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

DeleteUserConfirmDialog.defaultProps = {
  saving: false,
};

UserDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string,
  pendingUser: PropTypes.object,
  onUpdated: PropTypes.func,
};

UserDetailsModal.defaultProps = {
  userId: null,
  pendingUser: null,
  onUpdated: null,
};

export default UserDetailsModal;

